import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';

type ScheduleCategory = '예배' | '행사' | '모임' | '교육' | '기타';

const SCHEDULE_CATEGORIES: ScheduleCategory[] = ['예배', '행사', '모임', '교육', '기타'];

const CATEGORY_BADGE: Record<ScheduleCategory, string> = {
  예배: 'manage-badge--blue',
  행사: 'manage-badge--purple',
  모임: 'manage-badge--green',
  교육: 'manage-badge--amber',
  기타: 'manage-badge--gray',
};

type ScheduleRow = {
  id: number;
  category: ScheduleCategory;
  title: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  place: string;
  description: string | null;
};

type Holiday = { id: number; date: string; name: string };

type ScheduleMonth = { month: string; schedules: ScheduleRow[]; holidays: Holiday[] };

type ScheduleForm = {
  category: ScheduleCategory;
  title: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  place: string;
  description: string;
};

type ScheduleDay = { date: string; holidays: Holiday[]; schedules: ScheduleRow[] };

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const pad2 = (value: number) => String(value).padStart(2, '0');
const toYmd = (date: Date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const currentMonth = () => toYmd(new Date()).slice(0, 7);

function shiftMonth(month: string, delta: number) {
  const [y, m] = month.split('-').map(Number);
  const date = new Date(y, m - 1 + delta, 1);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;
}

function monthLabel(month: string) {
  const [y, m] = month.split('-').map(Number);
  return `${y}년 ${m}월`;
}

function weekdayIndex(ymd: string) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

function shortDate(ymd: string) {
  const [, m, d] = ymd.split('-').map(Number);
  return `${m}.${d}(${WEEKDAYS[weekdayIndex(ymd)]})`;
}

function scheduleTimeText(row: ScheduleRow) {
  const time = row.startTime ? `${row.startTime}${row.endTime ? ` ~ ${row.endTime}` : ''}` : '';
  if (row.startDate !== row.endDate) {
    const range = `${shortDate(row.startDate)} ~ ${shortDate(row.endDate)}`;
    return time ? `${range} · ${time}` : range;
  }
  return time || '종일';
}

// 여러 날짜 일정은 시작일(이전 달에 시작했으면 그 달 1일)에 한 번만 보인다 — 성도앱 목록과 같은 규칙.
function groupByDay(data: ScheduleMonth): ScheduleDay[] {
  const monthStart = `${data.month}-01`;
  const days = new Map<string, ScheduleDay>();
  const dayOf = (date: string) => {
    let day = days.get(date);
    if (!day) {
      day = { date, holidays: [], schedules: [] };
      days.set(date, day);
    }
    return day;
  };
  data.holidays.forEach((holiday) => dayOf(holiday.date).holidays.push(holiday));
  data.schedules.forEach((row) => dayOf(row.startDate < monthStart ? monthStart : row.startDate).schedules.push(row));
  return Array.from(days.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function emptyForm(date: string): ScheduleForm {
  return { category: '행사', title: '', startDate: date, endDate: date, startTime: '', endTime: '', place: '', description: '' };
}

function errorMessageOf(err: unknown, fallback: string) {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  return (data && typeof data === 'object' && 'message' in data && String(data.message)) || fallback;
}

export default function ManageSchedule({ authChurch }: { authChurch: string }) {
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState<ScheduleMonth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latestRequest = useRef('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ScheduleForm>(emptyForm(toYmd(new Date())));
  const [multiDay, setMultiDay] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchMonth = useCallback(
    (target: string) => {
      latestRequest.current = target;
      setLoading(true);
      setError(null);
      axios
        .get(`${MainURL}/church-schedule`, { params: { authChurch, month: target } })
        .then((res) => {
          if (latestRequest.current === target) setData(res.data?.data ?? null);
        })
        .catch((err) => {
          console.error('GET /church-schedule', err);
          if (latestRequest.current === target) setError('교회일정을 불러오지 못했습니다.');
        })
        .finally(() => {
          if (latestRequest.current === target) setLoading(false);
        });
    },
    [authChurch],
  );

  useEffect(() => {
    fetchMonth(month);
  }, [fetchMonth, month]);

  const days = useMemo(() => (data && data.month === month ? groupByDay(data) : []), [data, month]);
  const today = toYmd(new Date());
  const scheduleCount = data && data.month === month ? data.schedules.length : 0;
  const holidayCount = data && data.month === month ? data.holidays.length : 0;

  const openCreateForm = () => {
    setEditingId(null);
    setForm(emptyForm(month === currentMonth() ? today : `${month}-01`));
    setMultiDay(false);
    setFormError('');
    setFormOpen(true);
  };

  const openEditForm = (row: ScheduleRow) => {
    setEditingId(row.id);
    setForm({
      category: row.category,
      title: row.title,
      startDate: row.startDate,
      endDate: row.endDate,
      startTime: row.startTime,
      endTime: row.endTime,
      place: row.place,
      description: row.description ?? '',
    });
    setMultiDay(row.startDate !== row.endDate);
    setFormError('');
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
  };

  const submitForm = () => {
    const payload = { authChurch, ...form, endDate: multiDay ? form.endDate : form.startDate };
    if (!payload.title.trim() || !payload.startDate) return;
    setSaving(true);
    setFormError('');
    const request = editingId
      ? axios.put(`${MainURL}/church-schedule/${editingId}`, payload)
      : axios.post(`${MainURL}/church-schedule`, payload);

    request
      .then(() => {
        closeForm();
        // 저장한 일정이 있는 달로 이동해서 바로 확인할 수 있게 한다.
        const savedMonth = payload.startDate.slice(0, 7);
        if (savedMonth === month) fetchMonth(month);
        else setMonth(savedMonth);
      })
      .catch((err) => {
        console.error(editingId ? 'PUT /church-schedule/:id' : 'POST /church-schedule', err);
        setFormError(errorMessageOf(err, '저장하지 못했습니다.'));
      })
      .finally(() => setSaving(false));
  };

  const removeSchedule = (row: ScheduleRow) => {
    if (!window.confirm(`'${row.title}' 일정을 삭제할까요?`)) return;
    axios
      .delete(`${MainURL}/church-schedule/${row.id}`, { params: { authChurch } })
      .then(() => fetchMonth(month))
      .catch((err) => console.error('DELETE /church-schedule/:id', err));
  };

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">교회일정</h2>
          <p className="manage-panel-desc">성도용 앱 '교회일정' 탭에 날짜순 목록으로 표시되는 교회 일정을 관리합니다.</p>
        </div>
        <button type="button" className="manage-btn manage-btn--primary" onClick={openCreateForm}>
          + 새 일정 등록
        </button>
      </div>

      <div className="manage-filterbar manage-sched-monthbar">
        <button type="button" className="manage-btn manage-btn--sm" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="이전 달">
          ‹
        </button>
        <strong className="manage-sched-month">{monthLabel(month)}</strong>
        <button type="button" className="manage-btn manage-btn--sm" onClick={() => setMonth(shiftMonth(month, 1))} aria-label="다음 달">
          ›
        </button>
        {month !== currentMonth() && (
          <button type="button" className="manage-btn manage-btn--sm" onClick={() => setMonth(currentMonth())}>
            이번 달
          </button>
        )}
        <span className="manage-sched-count">
          일정 {scheduleCount}건 · 공휴일 {holidayCount}일
        </span>
      </div>

      {loading && <div className="manage-state">불러오는 중입니다...</div>}
      {!loading && error && <div className="manage-state manage-state--error">{error}</div>}

      {!loading && !error && (
        <div className="manage-grid-2col">
          <section className="manage-card-block">
            <div className="manage-block-head">
              <h3 className="manage-block-title">{monthLabel(month)} 일정</h3>
              <span className="manage-block-count">{scheduleCount}</span>
            </div>
            <div className="manage-sched-list">
              {days.length === 0 && <div className="manage-table-empty">{monthLabel(month)}에 등록된 일정이 없습니다.</div>}
              {days.map((day) => {
                const weekday = weekdayIndex(day.date);
                const tone = weekday === 0 || day.holidays.length > 0 ? ' is-red' : weekday === 6 ? ' is-blue' : '';
                return (
                  <div key={day.date} className={`manage-sched-day${day.date < today ? ' is-past' : ''}`}>
                    <div className={`manage-sched-date${tone}`}>
                      <b>{Number(day.date.slice(8, 10))}</b>
                      <span>{WEEKDAYS[weekday]}</span>
                      {day.date === today && <em>오늘</em>}
                    </div>
                    <div className="manage-sched-items">
                      {day.holidays.map((holiday) => (
                        <div key={`h-${holiday.id}`} className="manage-sched-holiday">
                          {holiday.name}
                          <small>공휴일</small>
                        </div>
                      ))}
                      {day.schedules.map((row) => (
                        <div key={row.id} className="manage-sched-item">
                          <div className="manage-sched-item-body">
                            <span className={`manage-badge ${CATEGORY_BADGE[row.category] ?? 'manage-badge--gray'}`}>{row.category}</span>
                            <p className="manage-sched-title">{row.title}</p>
                            <p className="manage-sched-meta">{[scheduleTimeText(row), row.place].filter(Boolean).join(' · ')}</p>
                            {!!row.description && <p className="manage-sched-desc">{row.description}</p>}
                          </div>
                          <div className="manage-row-actions">
                            <button type="button" className="manage-btn manage-btn--sm" onClick={() => openEditForm(row)}>수정</button>
                            <button type="button" className="manage-btn manage-btn--sm manage-btn--danger" onClick={() => removeSchedule(row)}>삭제</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <aside className="manage-side-stack">
            <div className="manage-side-card">
              <div className="manage-side-card-head">운영 팁</div>
              <div className="manage-side-card-body manage-tip-list">
                <div className="manage-tip">
                  <span className="manage-tip-dot" />
                  <span>성도용 앱에는 달력이 아니라 <b>날짜순 목록</b>으로 표시되며, 성도는 달을 넘겨 가며 볼 수 있습니다.</span>
                </div>
                <div className="manage-tip">
                  <span className="manage-tip-dot" />
                  <span>시간을 비워 두면 <b>종일</b> 일정으로, 종료일을 지정하면 기간 일정으로 표시됩니다.</span>
                </div>
                <div className="manage-tip">
                  <span className="manage-tip-dot" />
                  <span><b>공휴일</b>은 플랫폼에서 일괄 관리되어 모든 교회 앱에 자동으로 함께 표시됩니다. 여기서는 수정할 수 없습니다.</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}

      {formOpen && (
        <div className="manage-modal-overlay" onClick={closeForm}>
          <div className="manage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-modal-header">
              <h3>{editingId ? '일정 수정' : '새 일정 등록'}</h3>
              <button type="button" className="manage-modal-close" onClick={closeForm}>×</button>
            </div>
            <div className="manage-modal-body">
              <div className="manage-form manage-form--grid">
                <label className="manage-field">
                  <span>분류</span>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as ScheduleCategory })}>
                    {SCHEDULE_CATEGORIES.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </label>
                <label className="manage-field">
                  <span>장소</span>
                  <input value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} placeholder="예: 본당" />
                </label>
                <label className="manage-field manage-field--full">
                  <span>제목</span>
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="예: 추수감사절 연합예배" />
                </label>
                <label className="manage-field">
                  <span>{multiDay ? '시작일' : '날짜'}</span>
                  <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
                </label>
                {multiDay ? (
                  <label className="manage-field">
                    <span>종료일</span>
                    <input type="date" value={form.endDate} min={form.startDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
                  </label>
                ) : (
                  <div className="manage-field" />
                )}
                <label className="manage-field manage-field--full manage-sched-check">
                  <input
                    type="checkbox"
                    checked={multiDay}
                    onChange={(e) => {
                      setMultiDay(e.target.checked);
                      if (e.target.checked && form.endDate < form.startDate) setForm({ ...form, endDate: form.startDate });
                    }}
                  />
                  <span>여러 날에 걸친 일정</span>
                </label>
                <label className="manage-field">
                  <span>시작 시간 (선택)</span>
                  <input type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
                </label>
                <label className="manage-field">
                  <span>종료 시간 (선택)</span>
                  <input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
                </label>
                <label className="manage-field manage-field--full">
                  <span>설명</span>
                  <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </label>
              </div>
              {formError && <div className="manage-state manage-state--error">{formError}</div>}
            </div>
            <div className="manage-modal-footer">
              <button type="button" className="manage-btn" onClick={closeForm}>취소</button>
              <button
                type="button"
                className="manage-btn manage-btn--primary"
                onClick={submitForm}
                disabled={saving || !form.title.trim() || !form.startDate}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
